import { onBeforeUnmount, onMounted, watch, nextTick } from 'vue';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import { canUseDOM, isMobile } from '@/utils/dom-utils';

/**
 * 对 wrapper 元素的直接子节点批量应用进场/出场滚动动效。
 *
 * @param {import('vue').Ref<HTMLElement>} targetRef - 包裹目标区块的 wrapper 元素 ref
 * @param {() => any} [routeGetter] - 返回当前路由数据的 getter，用于路由切换后重建动效
 * @param {Object} [options]
 * @param {string} [options.start] - ScrollTrigger 起始位置。移动端默认 'top bottom+=50%'（元素未进入视口前 50% 视口高度即开始渐现，杜绝"透明空白"）；PC 默认 'top 85%'（元素顶部进入视口 15% 时触发）。
 * @param {string} [options.end='bottom top'] - ScrollTrigger 结束位置
 * @param {number} [options.y=30] - 进场时纵向偏移
 * @param {number} [options.blur=6] - 进场时模糊半径
 * @param {number} [options.duration=0.8] - 动画时长
 * @param {string} [options.ease='power2.out'] - 缓动函数
 * @param {string} [options.toggleActions='play none none reverse'] - 进出场行为：向下进入渐现/向下滚过保持显示/向上回进保持显示/向上离开渐隐重置
 */
export default function useRevealAnimations(targetRef, routeGetter, options = {}) {
  const opts = {
    end: options.end ?? 'bottom 15%',
    y: options.y ?? 50,
    blur: options.blur ?? 12,
    duration: options.duration ?? 0.8,
    ease: options.ease ?? 'power2.out',
    toggleActions: options.toggleActions ?? 'play none none reverse',
  };

  let tweens = [];

  const killAll = () => {
    if (!tweens.length) return;
    tweens.forEach((tween) => {
      tween.scrollTrigger?.kill();
      tween.kill();
    });
    tweens = [];
  };

  const setup = () => {
    if (!canUseDOM() || !targetRef?.value) return;
    killAll();

    const els = Array.from(targetRef.value.children).filter(
      (el) =>
        el.nodeType === 1 &&
        !el.hasAttribute('data-no-reveal') &&
        // 排除 GSAP pin 插入的辅助元素，避免给其加 transform/filter 破坏内部 fixed 定位
        !el.classList.contains('pin-spacer')
    );

    if (!els.length) return;

    // 移动端提前触发：元素未进入视口前 50% 视口高度即开始渐现，避免组件进场过晚导致空白；PC 保持 'top 85%' 不变
    const start = options.start ?? (isMobile() ? 'top bottom+=50%' : 'top 85%');
    // 首屏免动画：挂载/路由切换时已处于触发区内的元素（第一屏内容）直接呈现最终状态，避免加载动画导致首屏抖动
    const startRatio = options.start == null ? (isMobile() ? 1.5 : 0.85) : null;

    els.forEach((el) => {
      if (startRatio != null && el.getBoundingClientRect().top <= window.innerHeight * startRatio) {
        // 清除上一次动画可能残留的内联样式，恢复自然可见状态，且不创建 ScrollTrigger
        gsap.set(el, { opacity: 1, y: 0, filter: 'blur(0px)', clearProps: 'all' });
        return;
      }
      const tween = gsap.fromTo(
        el,
        { opacity: 0, y: opts.y, filter: `blur(${opts.blur}px)` },
        {
          opacity: 1,
          y: 0,
          filter: 'blur(0px)',
          duration: opts.duration,
          ease: opts.ease,
          scrollTrigger: {
            trigger: el,
            start,
            end: opts.end,
            toggleActions: opts.toggleActions,
          },
        }
      );
      tweens.push(tween);
    });

    ScrollTrigger.refresh();
  };

  onMounted(() => {
    if (!canUseDOM()) return;
    gsap.registerPlugin(ScrollTrigger);
    setup();
  });

  if (routeGetter) {
    watch(routeGetter, () => {
      nextTick(setup);
    });
  }

  onBeforeUnmount(killAll);

  return {
    refresh: () => canUseDOM() && ScrollTrigger.refresh(),
  };
}
